import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { ScoreBreakdown } from '@/types/survey';
import i18next from 'i18next';
import { basePath } from '@/integrations/supabase/client';

// Function to translate text
const t = i18next.t.bind(i18next);

export const generatePdf = async (
  scoreBreakdown: ScoreBreakdown,
  projectName: string,
  industry: string,
  language: string = 'en'
) => {
  const doc = new jsPDF();
  const currentDate = new Date().toLocaleDateString();
  
  // Set language - only try to change if i18next is initialized properly
  try {
    if (i18next.isInitialized) {
      i18next.changeLanguage(language);
    }
  } catch (error) {
    console.error('Error changing language:', error);
    // Continue with default translations if language change fails
  }

  // Function to add a header without logo
  const addHeader = () => {
    doc.setFontSize(20);
    doc.setTextColor(33, 33, 66); // Dark blue for header
    doc.text(t('results.title') || 'BEAMEvaluate Lite Assessment Report', 105, 25, { align: 'center' });
    
    // Add date info instead of version
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Date of Report Generated: ${currentDate}`, 105, 33, { align: 'center' });
    
    // Add separator line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(20, 37, 190, 37);
  };

  addHeader();

  // Add project and industry information with correct translation keys
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 80); // Dark blue-gray
  doc.text(t('project') || 'Project', 20, 50);
  doc.setFontSize(12);
  doc.text(projectName, 20, 58);

  doc.setFontSize(16);
  doc.setTextColor(40, 40, 80); // Dark blue-gray
  doc.text(t('industry') || 'Industry', 120, 50);
  doc.setFontSize(12);
  doc.text(industry, 120, 58);

  // Add report summary box
  doc.setFillColor(240, 240, 245); // Light gray with slight purple tint
  doc.roundedRect(20, 65, 170, 35, 3, 3, 'F');
  
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 100); // Dark blue
  doc.text(t('results.executiveSummary') || 'Executive Summary', 25, 75);
  doc.setFontSize(10);
  doc.setTextColor(50, 50, 50); // Dark gray
  
  const summary = scoreBreakdown.keyInsight || 
    'This BEAM Evaluate report provides a comprehensive assessment of your project\'s emotional and brand equity. The analysis is based on BaaBaa Group\'s proprietary brand valuation methodology.';
  
  doc.text(summary, 25, 83, { maxWidth: 160 });

  // ---- FIRST PAGE LAYOUT ----

  // Add metrics scaling explanations - adjusted position for the new layout
  doc.setFillColor(245, 245, 250);
  doc.roundedRect(20, 105, 170, 25, 3, 3, 'F');
  doc.setFontSize(11);
  doc.setTextColor(40, 40, 100);
  doc.text('Metrics Scaling Explanation:', 25, 113);
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 90);
  doc.text('BEAM Score: 0-100 scale. Higher values indicate stronger emotional and brand equity.', 25, 120);
  doc.text('NEV (Net Emotional Value): 0-50 scale. Represents overall emotional connection strength.', 25, 125);
  doc.text('EROI (Emotional Return on Investment): 0-5x scale. Measures leverage of emotional capital.', 25, 130);

  // Add scores in a more compact layout with uniform fonts
  const yPos = 135;
  
  // BEAM Score - with bold text and certification
  doc.setFillColor(240, 240, 250); // Light gray with blue tint
  doc.roundedRect(20, yPos, 50, 25, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 90); // Dark blue-gray
  doc.text(`${t('metrics.beamScore') || 'BEAM Score'}`, 25, yPos + 8);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold"); // Bold font
  doc.setTextColor(40, 40, 120); // Dark blue
  
  // Convert BEAM Score from 0-10 to 0-100 scale
  const beamScore100 = scoreBreakdown.overallScore ? scoreBreakdown.overallScore * 10 : 0;
  doc.text(`${beamScore100.toFixed(1)}`, 45, yPos + 18);
  doc.setFont("helvetica", "normal"); // Reset font
  
  // Add certification level if applicable (based on the scaled score)
  if (scoreBreakdown.certification) {
    doc.setFontSize(7);
    doc.setTextColor(40, 40, 100);
    doc.text(`BEAM ${scoreBreakdown.certification} Certified`, 45, yPos + 23);
  }
  
  // NEV Score
  doc.setFillColor(245, 240, 250); // Light gray with purple tint
  doc.roundedRect(80, yPos, 50, 25, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 90); // Dark blue-gray
  doc.text(`${t('metrics.nev') || 'NEV'}`, 85, yPos + 8);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold"); // Bold font
  doc.setTextColor(80, 40, 120); // Purple
  doc.text(`${scoreBreakdown.nev?.toFixed(2)}`, 105, yPos + 18);
  doc.setFont("helvetica", "normal"); // Reset font
  
  // EROI
  doc.setFillColor(240, 245, 245); // Light gray with slight green tint
  doc.roundedRect(140, yPos, 50, 25, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 90); // Dark blue-gray
  doc.text(`${t('metrics.eroi') || 'EROI'}`, 145, yPos + 8);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold"); // Bold font
  doc.setTextColor(40, 80, 80); // Dark teal
  doc.text(`${scoreBreakdown.eroi?.toFixed(2)}x`, 165, yPos + 18);
  doc.setFont("helvetica", "normal"); // Reset font
  
  // Add rating with styled box
  const getRatingColor = (rating) => {
    switch (rating) {
      case 'High Risk': return [180, 50, 50]; // More muted red
      case 'Competitive': return [200, 160, 40]; // More muted amber
      case 'Exceptional': return [40, 120, 40]; // More muted green
      default: return [100, 100, 100]; // Gray
    }
  };
  
  const ratingColor = getRatingColor(scoreBreakdown.rating);
  doc.setFillColor(ratingColor[0], ratingColor[1], ratingColor[2]);
  doc.roundedRect(20, yPos + 30, 170, 20, 3, 3, 'F');
  doc.setTextColor(255);
  doc.setFontSize(12);
  doc.text(`${t('results.ratingLabel') || 'Project Rating'}: ${scoreBreakdown.rating}`, 105, yPos + 42, { align: 'center' });

  // Define the table data with improved styling
  const tableData = [
    [t('metrics.emotional') || 'Emotional', scoreBreakdown.ea.percentage.toFixed(1) + '%', scoreBreakdown.ea.feedback],
    [t('metrics.legacy') || 'Legacy', scoreBreakdown.elv.percentage.toFixed(1) + '%', scoreBreakdown.elv.feedback],
    [t('metrics.friction') || 'Friction', scoreBreakdown.l.percentage.toFixed(1) + '%', scoreBreakdown.l.feedback],
    [t('metrics.trust') || 'Trust', scoreBreakdown.tb.percentage.toFixed(1) + '%', scoreBreakdown.tb.feedback],
    [t('metrics.continuity') || 'Continuity', scoreBreakdown.bc.percentage.toFixed(1) + '%', scoreBreakdown.bc.feedback],
  ];

  // Define the table headers
  const headers = [t('results.category') || 'Category', t('results.score') || 'Score', t('results.feedback') || 'Key Insight'];

  // Add the table to the document - with more professional styling
  (doc as any).autoTable({
    head: [headers],
    body: tableData,
    startY: yPos + 55,
    margin: { horizontal: 20 },
    columnStyles: {
      0: { cellWidth: 30, fontStyle: 'bold' },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 120 }
    },
    styles: {
      fontSize: 8,
      cellPadding: 3,
      overflow: 'linebreak',
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [40, 40, 80], // Dark blue
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      lineWidth: 0.1,
    },
    alternateRowStyles: {
      fillColor: [248, 248, 252], // Very light blue-gray
    }
  });

  // Add Strategic Recommendations - MOVED UP closer to the table
  const finalY = (doc as any).lastAutoTable.finalY || 200;
  
  doc.setFillColor(245, 245, 250); // Light gray with purple tint
  doc.roundedRect(20, finalY + 5, 170, 60, 3, 3, 'F');
  
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 100); // Dark blue
  doc.text('Strategic Recommendations', 105, finalY + 15, { align: 'center' });
  
  // Add recommendations with priority styles
  const priorityColors = [
    [220, 100, 100], // High - red
    [220, 180, 60],  // Medium - amber
    [100, 200, 100]  // Low - green
  ];
  
  doc.setFontSize(10);
  
  let recY = finalY + 25;
  scoreBreakdown.recommendations.slice(0, 3).forEach((recommendation, index) => {
    if (index < 3) {
      const color = priorityColors[index];
      // Priority indicator
      doc.setFillColor(color[0], color[1], color[2]);
      doc.circle(25, recY, 3, 'F');
      
      // Recommendation text
      doc.setTextColor(50, 50, 50); // Dark gray
      doc.setFont(index === 0 ? "helvetica" : "helvetica", index === 0 ? "bold" : "normal");
      doc.text(`${index + 1}. ${recommendation}`, 32, recY, { maxWidth: 155 });
      recY += 10;
    }
  });

  // Move disclaimer to the bottom of pages - but only add to the second page now
  const addDisclaimerFooter = (pageNum) => {
    // Set active page to the specified page number
    doc.setPage(pageNum);
    
    const pageHeight = doc.internal.pageSize.height;
    
    // Add disclaimer at the very bottom with adequate spacing
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80); // Medium gray
    doc.text(
      'DISCLAIMER: This report is provided for informational purposes only. While we strive for accuracy, ' +
      'BaaBaa Group makes no warranties about the completeness, reliability, or suitability of this information. ' +
      'Any reliance on this report is at your own risk. © ' + new Date().getFullYear() + ' BaaBaa Group Ltd.',
      20, pageHeight - 15, { maxWidth: 170 }
    );

    // Add contact information centered
    doc.setFontSize(8);
    doc.setTextColor(40, 40, 100); // Dark blue
    doc.text('For more information: jc@baabaa.com | www.baabaa.io', 105, pageHeight - 8, { align: 'center' });
  };

  // Add a new page for the detailed breakdown and what to expect
  doc.addPage();
  
  // Add header on new page without logo
  addHeader();

  // Section title with improved spacing
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 100); // Dark blue
  doc.text(t('results.detailedMetrics') || 'Detailed Metrics Analysis', 20, 50);
  
  // Add bright spots & red flags with improved styling
  doc.setFillColor(240, 250, 240); // Light green tint
  doc.roundedRect(20, 60, 80, 70, 3, 3, 'F');
  
  doc.setFontSize(12);
  doc.setTextColor(40, 100, 40); // Dark green
  doc.text(t('results.projectStrengths') || 'Project Strengths:', 25, 70);
  
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50); // Dark gray
  let brightY = 80;
  if (scoreBreakdown.submetrics?.elv && scoreBreakdown.submetrics.elv.brightSpots) {
    scoreBreakdown.submetrics.elv.brightSpots.forEach((spot, index) => {
      if (index < 5) { // Limit to 5 spots
        doc.text(`• ${spot}`, 25, brightY);
        brightY += 8;
      }
    });
  }
  
  // Create red flags box with improved styling
  doc.setFillColor(250, 240, 240); // Light red tint
  doc.roundedRect(110, 60, 80, 70, 3, 3, 'F');
  
  doc.setFontSize(12);
  doc.setTextColor(120, 40, 40); // Dark red
  doc.text(t('results.projectChallenges') || 'Project Challenges:', 115, 70);
  
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50); // Dark gray
  let redFlagY = 80;
  if (scoreBreakdown.submetrics?.elv && scoreBreakdown.submetrics.elv.redFlags) {
    scoreBreakdown.submetrics.elv.redFlags.forEach((flag, index) => {
      if (index < 5) { // Limit to 5 flags
        doc.text(`• ${flag}`, 115, redFlagY);
        redFlagY += 8;
      }
    });
  }

  // What to Expect from BEAMEvaluate Pro
  const proY = 135;
  doc.setFillColor(235, 235, 245); // Light purple-gray
  doc.roundedRect(20, proY, 170, 90, 3, 3, 'F');
  
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 100); // Dark blue
  doc.text('What to Expect from BEAMEvaluate Pro', 105, proY + 10, { align: 'center' });
  
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50); // Dark gray
  
  const proFeatures = [
    'Fully Automated AI Analysis: Advanced machine learning algorithms analyze emotional and brand metrics.',
    'Blockchain Integration: Secure, immutable record of brand evaluations stored on blockchain.',
    'Competitive Benchmarking: Compare your brand against industry standards and competitors.',
    'Predictive Analytics: AI-driven forecasting of potential growth and risk scenarios.',
    'Custom Strategic Recommendations: Tailored action items to improve your brand\'s emotional value.',
    'Real-time Market Sentiment: Continuous monitoring of brand perception across digital channels.',
    'Advanced Visualization Tools: Interactive 3D data representation and trend analysis.'
  ];
  
  let featureY = proY + 22;
  proFeatures.forEach(feature => {
    doc.text('• ' + feature, 25, featureY, { maxWidth: 160 });
    featureY += 8;
  });

  // Add BEAMEvaluate Pro explanation at the bottom
  doc.setFillColor(245, 245, 255); // Light bluish-purple
  doc.roundedRect(20, proY + 100, 170, 30, 3, 3, 'F');
  
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 100); // Dark blue
  doc.text('About BEAMEvaluate Professional', 25, proY + 108);
  doc.setFontSize(8);
  doc.setTextColor(50, 50, 50); // Dark gray
  doc.text(
    'This report was generated using BEAMEvaluate Lite. For advanced insights, upgrade to BEAMEvaluate Professional, ' +
    'BaaBaa Group\'s proprietary brand valuation methodology. The Pro version leverages AI and blockchain ' +
    'technology to provide deeper analysis and actionable recommendations with verifiable data integrity.',
    25, proY + 116, { maxWidth: 160 }
  );

  // Add disclaimer footer to ONLY the second page now
  addDisclaimerFooter(2); // Second page only

  // Save the PDF with project name
  doc.save(`${projectName}-assessment-report.pdf`);
};

// Helper function to draw progress bars for the metrics
const drawProgressBar = (doc: any, x: number, y: number, width: number, height: number, percent: number) => {
  // Draw background bar
  doc.setDrawColor(220, 220, 220);
  doc.setFillColor(220, 220, 220);
  doc.roundedRect(x, y, width, height, 2, 2, 'FD');
  
  // Draw progress
  doc.setDrawColor(59, 130, 246);
  doc.setFillColor(59, 130, 246);
  doc.roundedRect(x, y, width * (percent / 100), height, 2, 2, 'FD');
};
